import React from 'react';
import { Edit, Trash2, Shield, ArrowDownCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { formatCurrency, formatDate, formatStock } from '../../lib/utils';

const CATEGORY_STYLES = {
  'Antibiótico': { bg: 'bg-red-100', text: 'text-red-700' },
  'Vitamina': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Vacuna': { bg: 'bg-blue-100', text: 'text-blue-700' },
  'Desparasitante': { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  'Antiinflamatorio': { bg: 'bg-purple-100', text: 'text-purple-700' },
  'Analgésico': { bg: 'bg-pink-100', text: 'text-pink-700' },
  'Suplemento': { bg: 'bg-teal-100', text: 'text-teal-700' },
  'Hormonal': { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  'Protector Hepático': { bg: 'bg-lime-100', text: 'text-lime-700' },
  'Otros': { bg: 'bg-slate-100', text: 'text-slate-700' },
};
const catStyle = (c) => CATEGORY_STYLES[c] || CATEGORY_STYLES['Otros'];

const ADMIN_LABEL = { premix: 'Premix', oral: 'Oral', inyectable: 'Inyectable' };
const ADMIN_STYLE = {
  premix: 'bg-purple-100 text-purple-700',
  oral: 'bg-green-100 text-green-700',
  inyectable: 'bg-blue-100 text-blue-700',
};

function getMedStatus(m) {
  const isExpired = m.expiration_date && new Date(m.expiration_date) < new Date();
  const isExpiring = !isExpired && m.expiration_date && (() => {
    const diff = Math.ceil((new Date(m.expiration_date) - new Date()) / 86400000);
    return diff <= 30 && diff > 0;
  })();
  if (isExpired) return { label: 'Vencido', dot: 'bg-red-500' };
  if (m.current_stock <= m.min_stock) return { label: 'Stock Bajo', dot: 'bg-red-500' };
  if (isExpiring) return { label: 'Por Vencer', dot: 'bg-yellow-500' };
  return { label: 'Normal', dot: 'bg-green-500' };
}

const stockPct = (m) => {
  const min = m.min_stock || 0;
  if (min <= 0) return 100;
  return Math.min(100, Math.round((m.current_stock / (min * 3)) * 100));
};

function Actions({ m, onMovement, onEdit, onDelete, isSuperUser }) {
  return (
    <div className="flex items-center gap-0.5">
      <Button variant="ghost" size="sm" onClick={() => onMovement(m)} className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" title="Registrar movimiento">
        <ArrowDownCircle className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onEdit(m)} className={`h-8 w-8 p-0 hover:bg-blue-50 ${isSuperUser ? 'text-blue-600' : 'text-slate-400'}`} title="Editar">
        <Edit className="h-4 w-4" />
        {!isSuperUser && <Shield className="h-2.5 w-2.5 -ml-1 text-amber-500" />}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onDelete(m.id)} className={`h-8 w-8 p-0 hover:bg-red-50 ${isSuperUser ? 'text-red-600' : 'text-slate-400'}`} title="Eliminar">
        <Trash2 className="h-4 w-4" />
        {!isSuperUser && <Shield className="h-2.5 w-2.5 -ml-1 text-amber-500" />}
      </Button>
    </div>
  );
}

export function InventoryModernTableView({ medications, ...handlers }) {
  if (medications.length === 0) return <p className="text-center py-12 text-slate-500">No hay medicamentos registrados</p>;
  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="inventory-modern-table-view">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-900 text-white text-left">
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider">Medicamento</th>
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-right">Stock</th>
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-right hidden md:table-cell">Precio</th>
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center hidden lg:table-cell">Vencimiento</th>
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Estado</th>
              <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wider text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {medications.map((m, i) => {
              const st = getMedStatus(m);
              const cs = catStyle(m.category);
              return (
                <tr key={m.id} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i % 2 === 1 ? 'bg-slate-50/50' : 'bg-white'}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-xs ${cs.bg} ${cs.text}`}>
                        {m.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 uppercase text-xs truncate">{m.name}</p>
                        <p className="text-[11px] text-slate-400">
                          {m.category}
                          {m.administration_type && <span className={`ml-1.5 px-1.5 py-px rounded text-[9px] font-semibold ${ADMIN_STYLE[m.administration_type] || 'bg-slate-100'}`}>{ADMIN_LABEL[m.administration_type]}</span>}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="font-bold text-slate-900">{formatStock(m.current_stock)} <span className="font-normal text-slate-400 text-xs capitalize">{m.presentation || 'unid.'}</span></p>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <div className="w-16 h-1 bg-slate-200 rounded-full overflow-hidden">
                        <div className={`h-full ${m.current_stock <= m.min_stock ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${stockPct(m)}%` }} />
                      </div>
                      <span className="text-[9px] text-slate-400">min {formatStock(m.min_stock)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right hidden md:table-cell">
                    <p className="font-semibold text-slate-900">{formatCurrency(m.price_usd, 'USD')}</p>
                    <p className="text-xs text-green-600">{formatCurrency(m.price_dop)}</p>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-slate-500 hidden lg:table-cell">
                    {m.expiration_date ? formatDate(m.expiration_date) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700">
                      <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center"><Actions m={m} {...handlers} /></div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
